<template>
  <m-field
    :class="[{ 'flex-1': !width }, `text-field-${$.uid}`]"
    :data-testid="testId"
    class="text-field-wrapper"
    :status="hasError ? 'danger' : 'default'">
    <m-field-label slot="label" v-if="label" :data-testid="`${testId}-label`">
      {{ label }}

      <help-tooltip v-if="hint" :text="hint" />
    </m-field-label>
    <m-text-field
      @focus="handleFocus"
      @blur="onBlur"
      @change="syncValueFromField"
      class="text-field"
      @clear="$emit('clear')"
      :style="computedWidth"
      v-bind="{ ...removeEventsFromAttrs($attrs) }"
      @input="syncValueFromField"
      :name="fieldName"
      :placeholder="placeholder"
      :value="lazyValue"
      :disabled="disabled"
      @keydown="onKeyDown"
      @paste="onPaste"
      :type="type"
      :input-mode="type === 'number' ? 'numeric' : null"
      :id="testId">
      <span slot="leading" v-if="$slots.leading || leadingIcon" :data-testid="`${testId}-leading`">
        <!-- @slot Leading content inside input -->
        <slot v-if="$slots.leading" name="leading"></slot>
        <basic-icon v-if="leadingIcon" :color="leadingIcon.color" :size="leadingIcon.size">
          {{ leadingIcon.icon }}
        </basic-icon>
      </span>
      <span
        v-if="$slots.trailing || trailingIcon"
        :data-testid="`${testId}-trailing`"
        slot="trailing">
        <!-- @slot Trailing content inside input -->
        <slot v-if="$slots.trailing" name="trailing"></slot>
        <basic-icon v-if="trailingIcon" :color="trailingIcon.color" :size="trailingIcon.size">
          {{ trailingIcon.icon }}
        </basic-icon>
      </span>
    </m-text-field>
    <m-field-hint :data-testid="`${testId}-errors`" v-if="shouldDisplayMessages">
      {{ errorBucket.join(', ') }}
    </m-field-hint>
  </m-field>
</template>

<script>
import fieldMixin from '@/components/mixins/fieldMixin';
import validatable from '@/components/mixins/validatable';
import testable from '@/components/mixins/testable';
import { BasicIcon, HelpTooltip } from '@/components';
import { propValidatorHelper, removeEventsFromAttrs } from '@satellite/plugins/util';

/**
 * TextField wraps Mirdanda WC library's <m-field> and <m-text-field> components and is used to reduce the amount
 * of code written to create a field
 * @displayName Text Field
 */
export default {
  // Note for v-model and v-on="$listeners" issues - read https://github.com/vuejs/vue/issues/7042
  name: 'TextField',
  methods: {
    removeEventsFromAttrs,
    // Password managers fill the inner <input> with events that don't leave Miranda's shadow root
    // as `input` (or with no events at all), so also read the value on `change` and on blur.
    syncValueFromField(event) {
      if (event.target.value !== this.internalValue) {
        this.internalValue = event.target.value;
      }
    },
    onBlur(event) {
      this.syncValueFromField(event);
      this.handleBlur();
    },
    onKeyDown(event) {
      if (this.readonly) {
        event.preventDefault();
        return;
      }

      if (this.type !== 'number') {
        return;
      }

      const { key, ctrlKey, metaKey, target } = event;

      // Allow Shortcuts (Ctrl+V, Ctrl+C) and Navigation (Arrows, Backspace, Tab)
      if (ctrlKey || metaKey || key.length > 1) {
        return;
      }

      // Block Double Dots
      if (key === '.' && target.value.includes('.')) {
        event.preventDefault();
        return;
      }

      // Block invalid characters (Only 0-9 and . allowed)
      if (!/^[0-9.]$/u.test(key)) {
        event.preventDefault();
      }
    },

    onPaste(event) {
      if (this.readonly) {
        event.preventDefault();
        return;
      }

      if (this.type !== 'number') {
        return;
      }

      event.preventDefault();

      const text = (event.clipboardData || window.clipboardData).getData('text');
      let clean = text.replace(/[^0-9.]/gu, '');

      // Fix multiple dots in pasted content (keep first, remove rest)
      const parts = clean.split('.');
      if (parts.length > 2) {
        clean = `${parts.shift()}.${parts.join('')}`;
      }

      // execCommand is deprecated but it is the ONLY method that:
      // 1. Works on type="number" (where selection APIs fail)
      // 2. Automatically triggers 'input' events for Vue
      // 3. Inserts text exactly where the cursor is
      if (document.queryCommandSupported('insertText')) {
        document.execCommand('insertText', false, clean);
      } else {
        // Fallback for very old/odd browsers: simple replace
        event.target.value = clean;
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  },
  mixins: [fieldMixin, validatable, testable],
  components: { BasicIcon, HelpTooltip },
  props: {
    /**
     * @model
     */
    modelValue: {
      type: [String, Number],
      required: false
    },
    /**
     * Field Type
     */
    type: {
      type: String,
      required: false,
      default: 'text',
      validator: value => {
        return ['text', 'password', 'number', 'email'].includes(value);
      }
    },
    /**
     * Leading Icon
     * @values { icon: 'email', color: 'color-text-secondary', size: 'font-size-5' }
     */
    leadingIcon: {
      type: Object,
      required: false,
      validator: value => {
        const isValid = ['icon'].every(required => Object.keys(value).includes(required));
        return propValidatorHelper({
          value,
          isValid,
          componentName: 'TextField',
          propName: 'leadingIcon',
          message: ''
        });
      }
    },
    /**
     * Trailing Icon
     * @values { icon: 'email', color: 'color-text-secondary', size: 'font-size-3' }
     */
    trailingIcon: {
      type: Object,
      required: false,
      validator: value => {
        const isValid = ['icon'].every(required => Object.keys(value).includes(required));
        return propValidatorHelper({
          value,
          isValid,
          componentName: 'TextField',
          propName: 'trailingIcon',
          message: ''
        });
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  mounted() {
    this.util.appendStyleToShadowDomEl(
      document.querySelector(`.text-field-${this.$.uid} m-text-field`),
      'input',
      'font-size: 16px !important;border-radius: 4px;'
    );
  }
};
</script>

<style scoped lang="scss">
m-field,
.text-field {
  border-radius: 4px;
  @media (max-width: $tabletBreakpoint) {
    width: 100% !important;
  }
}

m-text-field {
  padding: 0 10px;
}
</style>
